import { useCallback, useEffect, useState } from 'react';
import { useApi, discoveryApiRef } from '@backstage/core-plugin-api';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import SortIcon from '@material-ui/icons/Sort';
import InsertEmoticonIcon from '@material-ui/icons/InsertEmoticon';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Popper from '@material-ui/core/Popper';
import Paper from '@material-ui/core/Paper';
import { MarketplaceItem } from './types';
import { HERO_GRADIENT } from './typeMetadata';
import { useMarketplaceFetchApi } from './marketplaceFetch';

// ─── Types ────────────────────────────────────────────────────────────────────

interface MarketplaceComment {
  id: number;
  artifact_name: string;
  artifact_type: string;
  parent_id: number | null;
  author: string;
  author_ref: string;
  body: string;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
}

export interface CommentsSectionProps {
  item: MarketplaceItem;
  /** Full Backstage entity ref, e.g. "user:default/jane.doe". Empty string when unauthenticated. */
  currentUserRef: string;
  /** True when the current user is a Marketplace admin — can edit/delete any comment. */
  isAdmin?: boolean;
}

// ─── Emoji picker data ───────────────────────────────────────────────────────

const EMOJI_CATEGORIES: { label: string; emojis: string[] }[] = [
  { label: 'Smileys', emojis: ['😀','😄','😊','🙂','😎','🤔','😅','😂','🥳','😍','🤩','👋','🙏','🤝'] },
  { label: 'Reactions', emojis: ['👍','👎','❤️','🔥','⭐','💯','✅','❌','⚡','💡','🎉','🚀','💪','👏'] },
  { label: 'Objects', emojis: ['🐛','🔧','📝','📌','🔗','📦','🏷️','🔒','🔑','📊','💻','🖥️','⚙️','📁'] },
];

// ── Per-user avatar colour palette ────────────────────────────────────────────

const AVATAR_COLORS = [
  '#6366f1', // indigo
  '#0ea5e9', // sky
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#f59e0b', // amber
  '#10b981', // emerald
  '#ef4444', // red
  '#14b8a6', // teal
  '#f97316', // orange
  '#a855f7', // purple
  '#06b6d4', // cyan
  '#84cc16', // lime
  '#e11d48', // rose
  '#3b82f6', // blue
];

function avatarColor(name: string): string {
  let hash = 0;
  const s = name || '?';
  for (let i = 0; i < s.length; i++) {
    hash = s.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme: Theme) => {
  const isDark = theme.palette.type === 'dark';
  const border = isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb';

  return {
    /* ── Root container — white surface over page background ────────────── */
    root: {
      width: '100%',
      background: isDark ? '#1e1e2e' : '#fff',
      borderRadius: 16,
      padding: theme.spacing(2.5, 3),
      boxShadow: isDark
        ? '0 2px 16px rgba(0,0,0,0.4)'
        : '0 1px 6px rgba(0,0,0,0.07)',
    },

    /* ── Comment input card (top) ──────────────────────────────────────── */
    inputCard: {
      border: `1px solid ${border}`,
      borderRadius: 12,
      marginBottom: theme.spacing(2),
      overflow: 'hidden',
    },
    inputToolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(0.75, 1.5),
      borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f3f4f6'}`,
    },
    toolbarIcons: {
      display: 'flex',
      alignItems: 'center',
      gap: 2,
    },
    submitBtn: {
      borderRadius: 20,
      textTransform: 'none' as const,
      fontWeight: 600,
      fontSize: '0.82rem',
      padding: '5px 22px',
      color: '#fff',
      boxShadow: 'none',
      transition: 'filter 0.15s, opacity 0.15s',
      '&:hover': { filter: 'brightness(0.88)', boxShadow: 'none' },
      // Keep text white even in MUI disabled state
      '&.Mui-disabled': { color: '#fff !important' },
    },
    cancelBtn: {
      borderRadius: 20,
      textTransform: 'none' as const,
      fontWeight: 600,
      fontSize: '0.82rem',
      padding: '5px 22px',
      color: isDark ? 'rgba(255,255,255,0.6)' : '#6b7280',
      borderColor: isDark ? 'rgba(255,255,255,0.18)' : '#d1d5db',
      boxShadow: 'none',
      transition: 'border-color 0.15s, color 0.15s',
      '&:hover': {
        borderColor: isDark ? 'rgba(255,255,255,0.35)' : '#9ca3af',
        background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
        boxShadow: 'none',
      },
    },

    /* ── Section header ────────────────────────────────────────────────── */
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(1.5),
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    headerTitle: {
      fontWeight: 700,
      fontSize: '1rem',
      color: theme.palette.text.primary,
    },
    countBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 24,
      height: 24,
      borderRadius: 12,
      fontSize: '0.72rem',
      fontWeight: 700,
      color: isDark ? '#9ca3af' : '#6b7280',
      background: isDark ? 'rgba(255,255,255,0.08)' : '#f3f4f6',
      padding: '0 7px',
    },
    sortLabel: {
      fontSize: '0.78rem',
      color: theme.palette.text.secondary,
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      gap: 4,
    },
    sortBtn: {
      fontSize: '0.78rem',
      color: theme.palette.text.secondary,
      fontWeight: 500,
      textTransform: 'none' as const,
      minWidth: 0,
      padding: '2px 6px',
      borderRadius: 6,
      '&:hover': { color: theme.palette.primary.main, background: theme.palette.action.hover },
    },

    /* ── Comment rows — flat, no card borders ──────────────────────────── */
    commentThread: {
      display: 'flex',
      flexDirection: 'column' as const,
    },
    threadBlock: {
      borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#f3f4f6'}`,
      '&:last-child': { borderBottom: 'none' },
    },
    replyToggleBtn: {
      fontSize: '0.76rem',
      fontWeight: 600,
      textTransform: 'none' as const,
      color: isDark ? 'rgba(255,255,255,0.45)' : '#6b7280',
      minWidth: 0,
      padding: '2px 6px',
      // Align with comment text body: avatar(28px) + gap(12px) = 40px
      marginLeft: 40,
      marginBottom: theme.spacing(0.75),
      '&:hover': { background: 'transparent', color: theme.palette.primary.main },
    },
    commentRow: {
      display: 'flex',
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.25, 0),
    },
    replyRow: {
      display: 'flex',
      gap: theme.spacing(1.5),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      // Indent replies to align with comment text body (avatar 28px + gap 12px = 40px)
      paddingLeft: 40,
      paddingRight: 0,
    },

    /* ── Avatars ───────────────────────────────────────────────────────── */
    avatar: {
      width: 28,
      height: 28,
      fontSize: '0.65rem',
      fontWeight: 700,
      letterSpacing: '0.02em',
      flexShrink: 0,
      color: '#fff',
    },
    replyAvatar: {
      width: 22,
      height: 22,
      fontSize: '0.55rem',
      fontWeight: 700,
      letterSpacing: '0.02em',
      flexShrink: 0,
      color: '#fff',
    },
    avatarDeleted: {
      background: `${theme.palette.action.disabledBackground} !important`,
    },

    /* ── Body ──────────────────────────────────────────────────────────── */
    commentBody: {
      flex: 1,
      minWidth: 0,
    },
    authorRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 2,
    },
    authorMeta: {
      display: 'flex',
      alignItems: 'baseline',
      gap: theme.spacing(1),
      minWidth: 0,
    },
    topActions: {
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      flexShrink: 0,
      marginLeft: theme.spacing(1),
    },
    authorName: {
      fontWeight: 600,
      fontSize: '0.83rem',
      color: theme.palette.text.primary,
      lineHeight: 1.3,
    },
    timestamp: {
      fontSize: '0.73rem',
      color: isDark ? 'rgba(255,255,255,0.38)' : '#9ca3af',
      lineHeight: 1.3,
    },
    editedBadge: {
      fontSize: '0.68rem',
      fontStyle: 'italic',
      color: isDark ? 'rgba(255,255,255,0.3)' : '#b0b7c3',
    },
    bodyText: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: isDark ? 'rgba(255,255,255,0.82)' : '#374151',
      marginTop: 2,
      whiteSpace: 'pre-wrap' as const,
      wordBreak: 'break-word' as const,
    },
    deletedText: {
      fontSize: '0.88rem',
      fontStyle: 'italic',
      color: theme.palette.text.disabled,
    },

    /* ── Action bar: Reply ••• ────────────────────────────────────────── */
    actionRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.25),
      marginTop: theme.spacing(0.5),
    },
    replyBtn: {
      fontSize: '0.74rem',
      fontWeight: 500,
      color: isDark ? 'rgba(255,255,255,0.42)' : '#9ca3af',
      textTransform: 'none' as const,
      padding: '1px 6px',
      minWidth: 0,
      borderRadius: 4,
      '&:hover': { color: theme.palette.primary.main, background: 'transparent' },
    },
    editBtn: {
      padding: 3,
      marginLeft: theme.spacing(0.25),
      color: isDark ? 'rgba(255,255,255,0.3)' : '#c4c9d4',
      '&:hover': { color: theme.palette.primary.main, background: 'transparent' },
    },
    deleteBtn: {
      padding: 3,
      color: isDark ? 'rgba(255,255,255,0.3)' : '#c4c9d4',
      '&:hover': { color: '#dc2626', background: 'transparent' },
    },

    /* ── Inline forms ──────────────────────────────────────────────────── */
    inlineForm: {
      marginTop: theme.spacing(1),
      display: 'flex',
      flexDirection: 'column' as const,
      gap: theme.spacing(1),
    },
    inlineFormBtns: {
      display: 'flex',
      gap: theme.spacing(1),
    },
    confirmDelete: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginTop: theme.spacing(0.75),
      padding: theme.spacing(1, 1.5),
      borderRadius: 10,
      background: isDark ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.04)',
      border: `1px solid ${isDark ? 'rgba(239,68,68,0.25)' : 'rgba(239,68,68,0.18)'}`,
    },
    confirmText: {
      fontSize: '0.82rem',
      color: theme.palette.text.secondary,
      flex: 1,
    },

    /* ── Misc ──────────────────────────────────────────────────────────── */
    emptyState: {
      textAlign: 'center' as const,
      padding: theme.spacing(6, 2),
    },
    emptyIcon: {
      fontSize: '3rem',
      marginBottom: theme.spacing(1.5),
    },
    charCount: {
      fontSize: '0.72rem',
      color: theme.palette.text.hint,
      textAlign: 'right' as const,
    },

    /* ── Emoji picker ──────────────────────────────────────────────────── */
    emojiBtn: {
      padding: 4,
      color: theme.palette.text.secondary,
      '&:hover': { color: theme.palette.warning.main },
    },
    emojiPicker: {
      padding: theme.spacing(1.5),
      maxWidth: 320,
      zIndex: theme.zIndex.tooltip,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 12,
      boxShadow: theme.shadows[6],
    },
    emojiCategory: {
      fontSize: '0.7rem',
      fontWeight: 700,
      color: theme.palette.text.secondary,
      textTransform: 'uppercase' as const,
      letterSpacing: 0.5,
      marginTop: theme.spacing(0.75),
      marginBottom: theme.spacing(0.25),
      '&:first-child': { marginTop: 0 },
    },
    emojiGrid: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: 2,
    },
    emojiItem: {
      width: 32,
      height: 32,
      minWidth: 0,
      padding: 0,
      fontSize: '1.15rem',
      borderRadius: 6,
      cursor: 'pointer',
      '&:hover': { background: theme.palette.action.hover },
    },
  };
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Extracts the short display name from a full entity ref or plain name. */
function displayName(authorField: string): string {
  if (!authorField || authorField === '[deleted]') return '[deleted]';
  const slashIdx = authorField.lastIndexOf('/');
  const raw = slashIdx >= 0 ? authorField.slice(slashIdx + 1) : authorField;
  // "jane.doe" → "Jane Doe"
  return raw
    .split('.')
    .map(p => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

/** Returns up to 2 initials, e.g. "Darshan A" → "DA". */
function avatarInitials(name: string): string {
  const d = displayName(name);
  if (!d || d === '[deleted]') return '?';
  const words = d.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return words[0].slice(0, 2).toUpperCase();
}

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Lightweight inline emoji picker anchored to a button. */
function EmojiPickerButton({ onSelect }: { onSelect: (emoji: string) => void }) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Insert emoji">
        <IconButton
          className={classes.emojiBtn}
          size="small"
          onClick={e => setAnchorEl(open ? null : e.currentTarget)}
        >
          <InsertEmoticonIcon style={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <Popper open={open} anchorEl={anchorEl} placement="top-start">
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Paper className={classes.emojiPicker}>
            {EMOJI_CATEGORIES.map(cat => (
              <Box key={cat.label}>
                <Typography className={classes.emojiCategory}>{cat.label}</Typography>
                <Box className={classes.emojiGrid}>
                  {cat.emojis.map(em => (
                    <Button
                      key={em}
                      className={classes.emojiItem}
                      onClick={() => { onSelect(em); setAnchorEl(null); }}
                    >
                      {em}
                    </Button>
                  ))}
                </Box>
              </Box>
            ))}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}

interface InlineEditorProps {
  initial: string;
  onSave: (body: string) => Promise<void>;
  onCancel: () => void;
  heroBg: string;
}

function InlineEditor({ initial, onSave, onCancel, heroBg }: InlineEditorProps) {
  const classes = useStyles();
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!value.trim()) return;
    setSaving(true);
    try {
      await onSave(value.trim());
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className={classes.inlineForm}>
      <Box position="relative">
        <TextField
          multiline
          minRows={3}
          maxRows={12}
          variant="outlined"
          size="small"
          value={value}
          onChange={e => setValue(e.target.value)}
          inputProps={{ maxLength: 5000 }}
          autoFocus
          fullWidth
        />
        <Box position="absolute" right={8} bottom={8}>
          <EmojiPickerButton onSelect={em => setValue(v => v + em)} />
        </Box>
      </Box>
      <Typography className={classes.charCount}>{value.length}/5000</Typography>
      <Box className={classes.inlineFormBtns}>
        <Button
          className={classes.submitBtn}
          style={{
            background: heroBg,
            opacity: saving || !value.trim() ? 0.55 : 1,
          }}
          size="small"
          variant="contained"
          disabled={saving || !value.trim()}
          onClick={handleSave}
        >
          {saving ? <CircularProgress size={14} style={{ color: '#fff' }} /> : 'Save'}
        </Button>
        <Button className={classes.cancelBtn} size="small" variant="outlined" disabled={saving} onClick={onCancel}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
}

interface ReplyFormProps {
  onPost: (body: string) => Promise<void>;
  onCancel: () => void;
  heroBg: string;
}

function ReplyForm({ onPost, onCancel, heroBg }: ReplyFormProps) {
  const classes = useStyles();
  const [value, setValue] = useState('');
  const [posting, setPosting] = useState(false);

  const handlePost = async () => {
    if (!value.trim()) return;
    setPosting(true);
    try {
      await onPost(value.trim());
      setValue('');
    } finally {
      setPosting(false);
    }
  };

  return (
    <Box className={classes.inlineForm}>
      <Box position="relative">
        <TextField
          multiline
          minRows={2}
          maxRows={8}
          variant="outlined"
          size="small"
          placeholder="Write a reply…"
          value={value}
          onChange={e => setValue(e.target.value)}
          inputProps={{ maxLength: 5000 }}
          autoFocus
          fullWidth
        />
        <Box position="absolute" right={8} bottom={8}>
          <EmojiPickerButton onSelect={em => setValue(v => v + em)} />
        </Box>
      </Box>
      <Typography className={classes.charCount}>{value.length}/5000</Typography>
      <Box className={classes.inlineFormBtns}>
        <Button
          className={classes.submitBtn}
          style={{
            background: heroBg,
            opacity: posting || !value.trim() ? 0.55 : 1,
          }}
          size="small"
          variant="contained"
          disabled={posting || !value.trim()}
          onClick={handlePost}
        >
          {posting ? <CircularProgress size={14} style={{ color: '#fff' }} /> : 'Reply'}
        </Button>
        <Button className={classes.cancelBtn} size="small" variant="outlined" disabled={posting} onClick={onCancel}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
}

// ─── Comment row ──────────────────────────────────────────────────────────────

interface CommentRowProps {
  comment: MarketplaceComment;
  isReply?: boolean;
  currentUserRef: string;
  isAdmin?: boolean;
  heroBg: string;
  onEdit: (id: number, body: string) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onReply?: (parentId: number, body: string) => Promise<void>;
}

function CommentRow({
  comment,
  isReply = false,
  currentUserRef,
  isAdmin = false,
  heroBg,
  onEdit,
  onDelete,
  onReply,
}: CommentRowProps) {
  const classes = useStyles();
  const [editing, setEditing] = useState(false);
  const [replying, setReplying] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isOwn = !!currentUserRef && currentUserRef === comment.author_ref;
  const canModify = isOwn || isAdmin;
  const isDeleted = comment.is_deleted;

  const handleEdit = async (body: string) => {
    await onEdit(comment.id, body);
    setEditing(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(comment.id);
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleReply = async (body: string) => {
    if (onReply) {
      await onReply(comment.id, body);
      setReplying(false);
    }
  };

  return (
    <Box className={isReply ? classes.replyRow : classes.commentRow}>
      <Avatar
        className={`${isReply ? classes.replyAvatar : classes.avatar} ${isDeleted ? classes.avatarDeleted : ''}`}
        style={isDeleted ? undefined : { background: avatarColor(comment.author) }}
      >
          {avatarInitials(comment.author)}
      </Avatar>

      <Box className={classes.commentBody}>
        {/* Author + timestamp + top-right edit/delete */}
        <Box className={classes.authorRow}>
          <Box className={classes.authorMeta}>
            <Typography className={classes.authorName}>
              {isDeleted ? '[deleted]' : displayName(comment.author)}
            </Typography>
            <Typography className={classes.timestamp}>
              {formatRelativeTime(comment.created_at)}
            </Typography>
            {comment.updated_at !== comment.created_at && !isDeleted && (
              <Typography component="span" className={classes.editedBadge}>(edited)</Typography>
            )}
          </Box>
          {!isDeleted && canModify && !editing && (
            <Box className={classes.topActions}>
              <Tooltip title="Edit">
                <IconButton
                  className={classes.editBtn}
                  size="small"
                  aria-label="edit comment"
                  onClick={() => { setEditing(true); setConfirmDelete(false); }}
                >
                  <EditOutlinedIcon style={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  className={classes.deleteBtn}
                  size="small"
                  aria-label="delete comment"
                  onClick={() => { setConfirmDelete(true); setEditing(false); }}
                >
                  <DeleteOutlineIcon style={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Body or inline editor */}
        {editing ? (
          <InlineEditor
            initial={comment.body}
            onSave={handleEdit}
            onCancel={() => setEditing(false)}
            heroBg={heroBg}
          />
        ) : (
          <Typography className={isDeleted ? classes.deletedText : classes.bodyText}>
            {isDeleted ? '[deleted]' : comment.body}
          </Typography>
        )}

        {/* Action bar: Reply only */}
        {!editing && !isDeleted && !isReply && onReply && currentUserRef && (
          <Box className={classes.actionRow}>
            <Button
              className={classes.replyBtn}
              startIcon={<ChatBubbleOutlineIcon style={{ fontSize: 12 }} />}
              onClick={() => setReplying(v => !v)}
              size="small"
            >
              Reply
            </Button>
          </Box>
        )}

        {/* Inline delete confirmation */}
        {confirmDelete && (
          <Box className={classes.confirmDelete}>
            <Typography className={classes.confirmText}>
              Delete this comment? This cannot be undone.
            </Typography>
            <Button
              size="small"
              variant="contained"
              style={{ background: '#dc2626', color: '#fff', minWidth: 0 }}
              disabled={deleting}
              onClick={handleDelete}
            >
              {deleting ? <CircularProgress size={12} /> : 'Delete'}
            </Button>
            <Button className={classes.cancelBtn} size="small" variant="outlined" disabled={deleting} onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
          </Box>
        )}

        {/* Inline reply form */}
        {replying && (
          <Box mt={1}>
            <ReplyForm onPost={handleReply} onCancel={() => setReplying(false)} heroBg={heroBg} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function CommentsSection({ item, currentUserRef, isAdmin = false }: CommentsSectionProps) {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useMarketplaceFetchApi();

  const [comments, setComments] = useState<MarketplaceComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newBody, setNewBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [sort, setSort] = useState<'newest' | 'oldest'>('newest');
  const [expandedReplies, setExpandedReplies] = useState<Set<number>>(new Set());
  const toggleReplies = (id: number) =>
    setExpandedReplies(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchComments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const base = await discoveryApi.getBaseUrl('marketplace');
      const url = `${base}/comments?artifact=${encodeURIComponent(item.name)}&type=${encodeURIComponent(item.type)}&sort=${sort}`;
      const resp = await fetchApi.fetch(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setComments(Array.isArray(data.comments) ? data.comments : []);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, fetchApi, item.name, item.type, sort]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // ── Mutations ──────────────────────────────────────────────────────────────

  const handlePost = async () => {
    const body = newBody.trim();
    if (!body) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const base = await discoveryApi.getBaseUrl('marketplace');
      const resp = await fetchApi.fetch(`${base}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          artifact_name: item.name,
          artifact_type: item.type,
          body,
          parent_id: null,
        }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data?.error?.message ?? `HTTP ${resp.status}`);
      }
      const created: MarketplaceComment = await resp.json();
      setNewBody('');
      // Insert without re-fetching — respects current sort order
      setComments(prev =>
        sort === 'newest' ? [created, ...prev] : [...prev, created],
      );
    } catch (e: any) {
      setSubmitError(e.message ?? 'Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (parentId: number, body: string) => {
    const base = await discoveryApi.getBaseUrl('marketplace');
    const resp = await fetchApi.fetch(`${base}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        artifact_name: item.name,
        artifact_type: item.type,
        body,
        parent_id: parentId,
      }),
    });
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      throw new Error(data?.error?.message ?? `HTTP ${resp.status}`);
    }
    const created: MarketplaceComment = await resp.json();
    // Append reply without re-fetching — tree grouping uses parent_id
    setComments(prev => [...prev, created]);
    // Auto-expand the parent's replies so the new reply is immediately visible
    setExpandedReplies(prev => new Set([...prev, parentId]));
  };

  const handleEdit = async (id: number, body: string) => {
    const base = await discoveryApi.getBaseUrl('marketplace');
    const resp = await fetchApi.fetch(`${base}/comments/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body }),
    });
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      throw new Error(data?.error?.message ?? `HTTP ${resp.status}`);
    }
    setComments(prev =>
      prev.map(c =>
        c.id === id ? { ...c, body, updated_at: new Date().toISOString() } : c,
      ),
    );
  };

  const handleDelete = async (id: number) => {
    const base = await discoveryApi.getBaseUrl('marketplace');
    const resp = await fetchApi.fetch(`${base}/comments/${id}`, {
      method: 'DELETE',
    });
    if (!resp.ok && resp.status !== 204) {
      const data = await resp.json().catch(() => ({}));
      throw new Error(data?.error?.message ?? `HTTP ${resp.status}`);
    }
    setComments(prev =>
      prev.map(c =>
        c.id === id
          ? { ...c, is_deleted: true, body: '[deleted]', author: '[deleted]', author_ref: '' }
          : c,
      ),
    );
  };

  // ── Tree grouping ──────────────────────────────────────────────────────────
  const topLevel = comments.filter(c => c.parent_id == null);
  const repliesByParent = comments
    .filter(c => c.parent_id != null)
    .reduce<Record<number, MarketplaceComment[]>>((acc, c) => {
      const key = c.parent_id!;
      if (!acc[key]) acc[key] = [];
      acc[key].push(c);
      return acc;
    }, {});

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box display="flex" alignItems="center" style={{ gap: 16 }} py={3}>
        <CircularProgress size={20} />
        <Typography color="textSecondary" style={{ fontSize: '0.875rem' }}>
          Loading comments…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        py={2}
        px={2}
        borderRadius={8}
        style={{ background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)' }}
      >
        <Typography style={{ color: '#dc2626', fontSize: '0.875rem' }}>
          Failed to load comments: {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* ── Comment input at top ── */}
      {currentUserRef && (
        <Box className={classes.inputCard}>
          <TextField
            multiline
            minRows={2}
            maxRows={8}
            variant="standard"
            placeholder="Add comment..."
            value={newBody}
            onChange={e => setNewBody(e.target.value)}
            inputProps={{
              maxLength: 5000,
              style: { padding: '14px 16px', fontSize: '0.9rem' },
            }}
            InputProps={{ disableUnderline: true }}
            disabled={submitting}
            fullWidth
          />
          <Box className={classes.inputToolbar}>
            <Box className={classes.toolbarIcons}>
              <EmojiPickerButton onSelect={em => setNewBody(v => v + em)} />
            </Box>
            <Button
              className={classes.submitBtn}
              style={{
                background: HERO_GRADIENT[item.type],
                opacity: !newBody.trim() || submitting ? 0.55 : 1,
              }}
              disabled={!newBody.trim() || submitting}
              onClick={handlePost}
              variant="contained"
            >
              {submitting ? <CircularProgress size={14} style={{ color: '#fff' }} /> : 'Submit'}
            </Button>
          </Box>
          {submitError && (
            <Typography style={{ color: '#dc2626', fontSize: '0.78rem', padding: '0 16px 8px' }}>
              {submitError}
            </Typography>
          )}
        </Box>
      )}

      {/* ── Header ── */}
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <Typography className={classes.headerTitle}>Comments</Typography>
          {comments.length > 0 && (
            <Typography component="span" className={classes.countBadge}>
              {comments.length}
            </Typography>
          )}
        </Box>
        <Button
          className={classes.sortBtn}
          startIcon={<SortIcon style={{ fontSize: 15 }} />}
          onClick={() => setSort(s => s === 'newest' ? 'oldest' : 'newest')}
          size="small"
        >
          {sort === 'newest' ? 'Most recent' : 'Oldest first'}
        </Button>
      </Box>

      {/* ── Thread list ── */}
      {topLevel.length === 0 ? (
        <Box className={classes.emptyState}>
          <Typography className={classes.emptyIcon} role="img" aria-label="speech bubble">
            💬
          </Typography>
          <Typography style={{ fontWeight: 600, marginBottom: 4 }}>No comments yet</Typography>
          <Typography variant="body2" color="textSecondary">
            Be the first to ask a question or leave feedback on this artifact.
          </Typography>
        </Box>
      ) : (
        <Box className={classes.commentThread}>
          {topLevel.map(comment => {
            const replies = repliesByParent[comment.id] ?? [];
            const isExpanded = expandedReplies.has(comment.id);
            return (
              <Box key={comment.id} className={classes.threadBlock}>
                <CommentRow
                  comment={comment}
                  currentUserRef={currentUserRef}
                  isAdmin={isAdmin}
                  heroBg={HERO_GRADIENT[item.type]}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onReply={handleReply}
                />
                {/* Reply count toggle */}
                {replies.length > 0 && (
                  <Button
                    className={classes.replyToggleBtn}
                    size="small"
                    onClick={() => toggleReplies(comment.id)}
                    startIcon={
                      isExpanded
                        ? <ExpandLessIcon style={{ fontSize: 16 }} />
                        : <ExpandMoreIcon style={{ fontSize: 16 }} />
                    }
                  >
                    {isExpanded
                      ? 'Hide replies'
                      : `${replies.length} ${replies.length === 1 ? 'reply' : 'replies'}`}
                  </Button>
                )}
                {/* Collapsible reply list */}
                <Collapse in={isExpanded} timeout="auto">
                  <Box>
                    {replies.map(reply => (
                      <CommentRow
                        key={reply.id}
                        comment={reply}
                        isReply
                        currentUserRef={currentUserRef}
                        isAdmin={isAdmin}
                        heroBg={HERO_GRADIENT[item.type]}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                      />
                    ))}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
